import React, { useEffect, useMemo, useState } from "react";
import { getStrings } from "../../Shared/Reservation/StringResources";
import { useDispatch } from "react-redux";
import { Alert, Button, Card, Form, Modal } from "react-bootstrap";
import { abortPayment, revertChanges, startPayment } from "./Actions";
import { getApiCallState, useStateSelector } from "./Model";
import { SpinnerSmall } from "../../Shared/Components/Spinner";
import { IPaymentFields } from "../Models";
import { getSettings } from "../Settings";
import { FormProvider, useForm } from "react-hook-form";
import { Payment } from "../PassengersAndPayment/Payment";
import { FakePaymentDialog } from "../Payment/FakePayment";
import { AdyenDialog } from "../Payment/Adyen";
import { Price } from "../Components/Price";
import SvgGift from "../../Shared/Icons/bootstrap/Gift";
import { Vouchers } from "../PassengersAndPayment/Vouchers";
import dayjs from "dayjs";
import { getErrorMessages } from "../Shared/Utils";
import { RedirectModal } from "../Components/RedirectModal";
import { DiscountCodes } from "../PassengersAndPayment/DiscountCodes";
import { clearApiCallState } from "../ApiCalls/Actions";

const strings = getStrings().component.completePayment;
const bookingStrings = getStrings().component.booking;
const orderDetailStrings = getStrings().component.orderDetails;



export const CompletePayment = () => {
	const formMethods = useForm<IPaymentFields>({ mode: "onBlur" });
	const dispatch = useDispatch();

	const settings = getSettings();
	const allowFakePayments = settings.allowFakePayments;

	const state = useStateSelector(state => state.manageBooking);

	const apicalls = getApiCallState();
	const startPaymentState = apicalls.getLoadingState("startPayment");
	const startPaymentError = useMemo(() => getErrorMessages("startPayment", apicalls), [startPaymentState]);
	const revertState = apicalls.getLoadingState("revertChanges");

	const paymentState = state.payment;
	const voucherError = apicalls.getError("verifyVoucher");
	const discountCodeError = apicalls.getError("verifyDiscountCode");

	const allowRevert = state.booking?.canBeReverted;

	const [showVouchers, setShowVouchers] = useState(state.booking?.vouchers?.length > 0);
	const [showRevertModal, setShowRevertModal] = useState(false);

	const showDiscountCodes = useMemo(() =>
	{
		return state.booking?.vouchers?.length <= 0
	}, [state.booking?.vouchers]);

	const remainingAmount = useMemo(() =>
	{
		const totalPriceToBePaid = state.booking?.totalPriceToBePaid || 0;

		const discountCodePercent = state.discountCodes?.[0]?.percent || 0;
		const discountCodeAmount = ((totalPriceToBePaid * discountCodePercent / 100) + state.discountCodes?.[0]?.amount)|| 0;

		const voucherAmount = state.vouchers?.reduce((acc, curr) => acc + curr.amount, 0) || 0;

		return Math.max(totalPriceToBePaid - discountCodeAmount - voucherAmount, 0);
	}, [state.booking, state.vouchers, state.discountCodes]);

	const travelDates = useMemo(() => {
		const dates = [] as string[];
		if (state.booking?.outbound?.departure)
			dates.push(dayjs(state.booking.outbound.departure).format("YYYY-MM-DD"));
		if (state.booking?.inbound?.departure)
			dates.push(dayjs(state.booking.inbound.departure).format("YYYY-MM-DD"));
		return dates;
	}, [state.booking]);

	const allVouchers = useMemo(() => {
		return (state.booking?.vouchers||[]).concat(state.vouchers||[]);
	}, [state.booking?.vouchers, state.vouchers]);

	const allDiscountCodes = useMemo(() => {
		return (state.booking?.discountCodes||[]).concat(state.discountCodes||[]);
	}, [state.booking?.discountCodes, state.discountCodes]);

	const paymentWithVouchersOnly = remainingAmount === 0 && state.vouchers?.length > 0;

	const submit = (data: IPaymentFields) => {
		console.debug("submit payment", data);

		if (data.paymentMethod || paymentWithVouchersOnly)
			dispatch(startPayment(state.booking.bookingNumber, data.paymentMethod ?? "none", state.vouchers, state.discountCodes));
	};

	const revertClicked = () => {
		dispatch(revertChanges(state.booking.bookingNumber));
	}

	useEffect(() => {
		return () => {
			setShowVouchers(false);
		};
	}, []);

	useEffect(() => {
		if (revertState === "loaded")
			setShowRevertModal(false);
	}, [revertState]);

	return <>
		<Card className="mt-3" id="complete-payment">
			<Card.Body>
				<FormProvider {...formMethods}>
					<Form onSubmit={formMethods.handleSubmit(submit)}>
						<Card.Title>{strings.heading}</Card.Title>
						<Card.Text>
							{strings.infoText}
						</Card.Text>

						{ showDiscountCodes && <DiscountCodes bookingNumber={state.booking.bookingNumber} email={state.booking.customer?.email} vouchers={allDiscountCodes} error={discountCodeError} remainingAmount={remainingAmount} travelDates={travelDates} /> }
						{ showVouchers && <Vouchers bookingNumber={state.booking.bookingNumber} email={state.booking.customer?.email} vouchers={allVouchers} error={voucherError} remainingAmount={remainingAmount} travelDates={travelDates} /> }

						{ remainingAmount > 0 && <section className="payment">
							<h2>{orderDetailStrings.paymentTypeHeading}</h2>

							{ !showVouchers &&
									<div><SvgGift /> <Button variant="link" onClick={() => setShowVouchers(true)}>{orderDetailStrings.vouchers.showButtonText}</Button></div> }

							<Payment settings={getSettings()} />
						</section>}

						{ paymentWithVouchersOnly && <Alert variant="info"><span dangerouslySetInnerHTML={ { __html: orderDetailStrings.vouchers.remainingAmountInfo } }></span></Alert> }

						<Alert variant="danger" show={startPaymentState === "error"}>
							{startPaymentError ? <ul className="list-unstyled mb-0">{startPaymentError.map((x, idx) => <li key={idx}>{x}</li>)}</ul> : bookingStrings.paymentError}
						</Alert>

						<Button variant="primary" disabled={startPaymentState === "loading"} type="submit" className="mt-3">
							{paymentWithVouchersOnly ? bookingStrings.completePaymentVouchersOnly : <>{bookingStrings.completePayment} (<Price amount={remainingAmount} />)</>} {startPaymentState === "loading" && <SpinnerSmall />}
						</Button>

						{ allowRevert && <Button variant="outline-secondary" onClick={() => setShowRevertModal(true)} className="mt-3 ml-3">Avbryt ändringar</Button>}
					</Form>
				</FormProvider>
			</Card.Body>
		</Card>
		{ allowFakePayments &&
			<FakePaymentDialog show={paymentState?.method === "fake"} bookingNumber={state.booking.bookingNumber}
				amount={paymentState?.amount} onCancel={() => dispatch(abortPayment())} /> }
		{ paymentState?.method === "adyen" &&
			<AdyenDialog amount={paymentState?.amount} clientKey={paymentState.adyenData.checkoutKey} environment={paymentState.adyenData.postData.environment}
				paymentId={paymentState.adyenData.sessionId} sessionData={paymentState.adyenData.postData.sessionData} bookingNumber={state.booking.bookingNumber}
				onPaymentAborted={() => dispatch(abortPayment())}/>}

		<RedirectModal show={state.payment?.method === "none" && state.payment?.amount === 0}
			redirectUrl={`${settings.receiptUrl}?bookingNumber=${state.booking.bookingNumber}&paymentReference=${state.vouchers?.length > 0 ? state.vouchers[0].voucherCode : ""}`}
			message={<><h4>{strings.paymentCompleteHeading}</h4>{strings.paymentCompleteText}</>}
			redirectAfterSeconds={10}
			/>

		{ allowRevert && <Modal show={showRevertModal} centered backdrop="static" onExited={() => dispatch(clearApiCallState("revertChanges"))}>
			<Modal.Header>
				<Modal.Title>Avbryt ändringar</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				Vill du avbryta dina pågående ändringar i bokingen och återställa den till tidigare läge?<br />
				Alla obetalda tillval kommer att tas bort.

				<Alert variant="danger" show={revertState === "error"} className="mt-3">Det gick inte att avbryta ändringarna.</Alert>
			</Modal.Body>
			{ revertState === "loading" ? <Modal.Footer>
				Återställer bokning... <SpinnerSmall />
			</Modal.Footer> :
			<Modal.Footer>
				<Button variant="success" onClick={revertClicked}>Ja, avbryt ändringarna</Button>
				<Button variant="outline-secondary" onClick={() => setShowRevertModal(false)}>Nej, behåll ändringarna</Button>
			</Modal.Footer> }
		</Modal>}
	</>
}